import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { VisitsService } from '@services/requests/housien-services/VisitsService';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import type { Visit } from '@services/requests/housien-models/Visit';
import { buildFeedbackNotes } from '@/features/visits/pages/ManageVisitsPage.useManageVisits.helpers';

type VisitStatus = NonNullable<Visit['status']>;

interface UseVisitDetailActionsArgs {
  visit: Visit;
  agencyId: string;
  onEdit: () => void;
  /** Notifies the list that opened this detail, so its own copy is refetched. */
  onUpdated?: () => void;
}

export interface VisitDetailActions {
  canConfirm: boolean;
  canCancel: boolean;
  canMarkDone: boolean;
  isMarkDoneBlocked: boolean;
  canReschedule: boolean;
  handleConfirm: () => Promise<void>;
  handleCancel: () => void;
  handleMarkDone: () => void;
  handleReschedule: () => void;
  cancelDialogOpen: boolean;
  setCancelDialogOpen: (v: boolean) => void;
  cancelReason: string;
  setCancelReason: (v: string) => void;
  confirmCancel: () => Promise<void>;
  feedbackDialogOpen: boolean;
  setFeedbackDialogOpen: (v: boolean) => void;
  feedbackRating: number;
  setFeedbackRating: (v: number) => void;
  feedbackObs: string;
  setFeedbackObs: (v: string) => void;
  confirmFeedback: () => Promise<void>;
}

/**
 * Computes allowed actions based on visit status and timing.
 */
function derivePermissions(status: VisitStatus, visitDatetimePassed: boolean) {
  return {
    canConfirm: status === 'pending',
    canCancel: status === 'pending' || status === 'confirmed',
    canMarkDone: status === 'confirmed',
    isMarkDoneBlocked: !visitDatetimePassed,
    canReschedule: status !== 'done',
  };
}

/**
 * Returns true when the visit datetime is in the past.
 */
function hasVisitDatetimePassed(visit: Visit): boolean {
  const dt = visit.visit_datetime;
  if (!dt) return true;
  return new Date(dt) <= new Date();
}

/**
 * Manages cancel dialog state and the API call to cancel a visit.
 */
function useCancelDialog(agencyId: string, visitId: string, invalidate: () => void, t: (key: string) => string) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const handleCancel = useCallback(() => {
    setCancelReason('');
    setCancelDialogOpen(true);
  }, []);

  const confirmCancel = useCallback(async () => {
    try {
      await VisitsService.updateVisit(agencyId, visitId, {
        status: 'cancelled',
        notes: cancelReason.trim() || undefined,
      });
      invalidate();
      setCancelDialogOpen(false);
      notifications.show({ color: 'orange', message: t('kanban.notifications.cancelled') });
    } catch { /* global error handler */ }
  }, [agencyId, visitId, cancelReason, invalidate, t]);

  return { cancelDialogOpen, setCancelDialogOpen, cancelReason, setCancelReason, handleCancel, confirmCancel };
}

/**
 * Manages feedback dialog state and the API call to submit post-visit feedback.
 */
function useFeedbackDialog(agencyId: string, visitId: string, invalidate: () => void, t: (key: string) => string) {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackObs, setFeedbackObs] = useState('');

  const confirmFeedback = useCallback(async () => {
    try {
      const payload = {
        status: 'done' as const,
        ...buildFeedbackNotes(feedbackRating, feedbackObs),
      };
      await VisitsService.updateVisit(agencyId, visitId, payload);
      invalidate();
      setFeedbackDialogOpen(false);
      notifications.show({ color: 'green', message: t('kanban.notifications.markedDone') });
    } catch { /* global error handler */ }
  }, [agencyId, visitId, feedbackRating, feedbackObs, invalidate, t]);

  return { feedbackDialogOpen, setFeedbackDialogOpen, feedbackRating, setFeedbackRating, feedbackObs, setFeedbackObs, confirmFeedback };
}

/**
 * Encapsulates the status-transition actions available for a visit detail page.
 * Determines which actions are allowed based on the current status and provides
 * handlers for each transition, including cancel dialog and feedback dialog.
 */
export function useVisitDetailActions({
  visit,
  agencyId,
  onEdit,
  onUpdated,
}: UseVisitDetailActionsArgs): VisitDetailActions {
  const { t } = useTranslation('@pages/visits');
  const queryClient = useQueryClient();
  const status = (visit.status ?? 'pending') as VisitStatus;
  const visitId = visit.visit_id ?? '';

  const permissions = derivePermissions(status, hasVisitDatetimePassed(visit));

  // The board that opened this modal keeps its own visit array -- it is not
  // react-query state, so invalidating the detail key left the card sitting in
  // the old column until a reload. Every transition tells the list as well.
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['visit-detail', agencyId, visitId] });
    onUpdated?.();
  }, [queryClient, agencyId, visitId, onUpdated]);

  const cancel = useCancelDialog(agencyId, visitId, invalidate, t);
  const feedback = useFeedbackDialog(agencyId, visitId, invalidate, t);

  const handleConfirm = useCallback(async () => {
    try {
      await VisitsService.updateVisit(agencyId, visitId, { status: 'confirmed' });
      invalidate();
      notifications.show({ color: 'green', message: t('kanban.notifications.confirmed') });
    } catch { /* global error handler */ }
  }, [agencyId, visitId, invalidate, t]);

  const handleMarkDone = useCallback(() => {
    if (permissions.isMarkDoneBlocked) {
      notifications.show({ color: 'orange', message: t('kanban.feedback.markDone.futureError') });
      return;
    }
    feedback.setFeedbackRating(0);
    feedback.setFeedbackObs('');
    feedback.setFeedbackDialogOpen(true);
  }, [permissions.isMarkDoneBlocked, t, feedback]);

  const handleReschedule = useCallback(() => {
    onEdit();
  }, [onEdit]);

  return {
    ...permissions,
    handleConfirm,
    handleCancel: cancel.handleCancel,
    handleMarkDone,
    handleReschedule,
    cancelDialogOpen: cancel.cancelDialogOpen,
    setCancelDialogOpen: cancel.setCancelDialogOpen,
    cancelReason: cancel.cancelReason,
    setCancelReason: cancel.setCancelReason,
    confirmCancel: cancel.confirmCancel,
    feedbackDialogOpen: feedback.feedbackDialogOpen,
    setFeedbackDialogOpen: feedback.setFeedbackDialogOpen,
    feedbackRating: feedback.feedbackRating,
    setFeedbackRating: feedback.setFeedbackRating,
    feedbackObs: feedback.feedbackObs,
    setFeedbackObs: feedback.setFeedbackObs,
    confirmFeedback: feedback.confirmFeedback,
  };
}
